import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, hotComics, latestArticles } from '@/mocks/home';
import SEO from '@/components/base/SEO';

const allComics = hotComics.map((comic) => {
  const matchingArticle = latestArticles.find((a) => a.category === comic.category);
  return {
    ...comic,
    articleSlug: matchingArticle ? matchingArticle.slug : null,
    articleTitle: matchingArticle ? matchingArticle.title : null,
  };
});

const categoryInfoMap: Record<string, { name: string; icon: string; description: string }> = {};
categories.forEach((c) => {
  categoryInfoMap[c.slug] = { name: c.name, icon: c.icon, description: c.description };
});

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'romance');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug]);

  const filteredComics = allComics.filter((c) => c.category === activeSlug);
  const currentCategory = categoryInfoMap[activeSlug] || categoryInfoMap['romance'];

  return (
    <div className="min-h-screen bg-background-50 pt-20">
      <SEO
        title={`${currentCategory.name}漫画 - 免费韩国漫画在线阅读 - 韩漫世界`}
        description={`韩漫世界${currentCategory.name}漫画分类，精选${currentCategory.description}，海量免费韩国漫画等你来阅读，流畅下拉式体验。`}
        keywords={`${currentCategory.name}漫画,免费韩漫,韩国漫画在线阅读,下拉式漫画`}
        canonicalUrl={`https://www.hanmanworld.com/category/${activeSlug}`}
      />
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-10">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
          <span>/</span>
          <span className="text-foreground-900 font-medium whitespace-nowrap">{currentCategory.name}漫画</span>
        </nav>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
              <i className={`${currentCategory.icon} text-primary-600`}></i>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
              {currentCategory.name}漫画
            </h1>
          </div>
          <p className="text-sm text-foreground-500 ml-12">{currentCategory.description} - 精选推荐</p>
        </div>

        <div className="flex items-center gap-2 mb-8 flex-wrap">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer ${
                activeSlug === cat.slug
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-xs`}></i>
                </div>
                {cat.name}
              </span>
            </Link>
          ))}
        </div>

        {filteredComics.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {filteredComics.map((comic) => (
              <Link
                key={comic.id}
                to={comic.articleSlug ? `/article/${comic.articleSlug}` : `/article/${comic.slug}`}
                className="group cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-lg mb-3" style={{ aspectRatio: '3/4' }}>
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                    <div className="flex items-center gap-1 text-white/90">
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-star-fill text-xs" style={{ color: 'oklch(var(--accent-400))' }}></i>
                      </div>
                      <span className="text-xs font-medium">{comic.rating}</span>
                    </div>
                  </div>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 mb-1 whitespace-nowrap overflow-hidden text-ellipsis">
                  {comic.title}
                </h3>
                <p className="text-xs text-foreground-500 line-clamp-1">{comic.description}</p>
                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-1.5 py-0.5 text-xs rounded bg-secondary-100 text-secondary-700 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-foreground-500">该分类暂无作品，请浏览其他分类</p>
          </div>
        )}
      </div>
    </div>
  );
}